import { buildWorkloadRows } from '../entities/course-template.entity.js';
// ─── CCD prefill (Phase E — CAP, PR2) ───────────────────────────────────────
// Deterministic mapping CourseTemplate → CcdDraft. No AI call here: the
// template was already populated by the syllabus AI import, so the draft just
// reshapes existing content into the TMAI CCD fields. The draft is a PROPOSAL
// — the editor applies it only on explicit user action, never auto-saves.
/** expectedHours rows are free-text labels; bucket them into the five TMAI
 *  time-allocation activities by keyword (es/en, case/accent-insensitive). */
const BUCKET_PATTERNS = [
    { bucket: 'lecture', pattern: /clase|video|sesion|lecture|class/ },
    { bucket: 'reading', pattern: /lectura|reading/ },
    { bucket: 'projects', pattern: /proyecto|project|investigacion|research/ },
    { bucket: 'exams', pattern: /examen|exam|quiz|prueba|evaluacion/ },
    // homework is the catch-all below; keep explicit markers anyway.
    { bucket: 'homework', pattern: /tarea|homework|escritura|writing|ensayo|essay|asignacion/ },
];
const normalize = (s) => s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
function bucketFor(label) {
    const clean = normalize(label);
    for (const { bucket, pattern } of BUCKET_PATTERNS) {
        if (pattern.test(clean))
            return bucket;
    }
    return 'homework';
}
/** Extract plain text from a TipTap JSON doc (text nodes joined; paragraphs
 *  become line breaks). Tolerates any malformed shape by returning ''. */
export function tiptapToPlainText(doc) {
    const parts = [];
    const walk = (node) => {
        if (!node || typeof node !== 'object')
            return;
        const n = node;
        if (typeof n.text === 'string')
            parts.push(n.text);
        if (Array.isArray(n.content)) {
            for (const child of n.content)
                walk(child);
            if (n.type === 'paragraph')
                parts.push('\n');
        }
    };
    walk(doc);
    return parts
        .join('')
        .replace(/\n{2,}/g, '\n')
        .trim();
}
/**
 * Builds the CCD prefill draft from a course template snapshot.
 *
 * - `courseNumber` ← template code.
 * - `description` ← plain text of the TipTap description.
 * - CLOs ← course objectives (proposed at undergraduate level; the teacher
 *   writes the graduate variant).
 * - `timeAllocation` ← las filas de la tabla de horas, clasificadas por rótulo,
 *   propuestas a nivel undergraduate (el editor copia a graduate a pedido).
 */
export function buildCcdDraft(template) {
    const allocation = {
        lecture: 0,
        reading: 0,
        homework: 0,
        projects: 0,
        exams: 0,
    };
    let anyHours = false;
    // LAS DOS listas: las horas que declara cada evaluación y las que no se
    // califican. Leer sólo `expectedHours` dejaría fuera el trabajo evaluado y el
    // CCD declararía menos horas de las que el curso exige — un subconteo de TCU
    // frente al acreditador, que es peor que no declarar nada.
    const { rows } = buildWorkloadRows(template.grading?.components, template.grading?.expectedHours);
    for (const row of rows) {
        allocation[bucketFor(row.label)] += row.hours;
        anyHours = true;
    }
    const description = tiptapToPlainText(template.description);
    const courseObjectives = (template.objectives?.course ?? [])
        .map((o) => o.text?.trim())
        .filter((t) => !!t);
    const draft = {
        clos: { undergraduate: courseObjectives, graduate: [] },
        timeAllocation: anyHours ? { undergraduate: allocation } : {},
    };
    if (template.code?.trim())
        draft.courseNumber = template.code.trim();
    if (description)
        draft.description = description;
    if (template.source?.type === 'pdf-import' && template.source.importedFrom) {
        draft.sourceImportedFrom = template.source.importedFrom;
    }
    return draft;
}
//# sourceMappingURL=cap-ccd.service.js.map